import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { FocusHeader } from '../components/FocusHeader';
import { Mail, Edit3, Newspaper } from 'lucide-react';

export function CreateCampaignChoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasContacts, setHasContacts] = useState(false);

  useEffect(() => {
    if (user) {
      checkContacts();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const checkContacts = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { count } = await supabase
        .from('email_contacts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('subscribed', true);

      setHasContacts((count || 0) > 0);
    } catch (err) {
      console.error('Error checking contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#E94E87] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasContacts) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Nieuwe Campaign"
          onBack={() => navigate('/dashboard')}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-[#231F20] mb-4">
              Geen Contacts Gevonden
            </h2>
            <p className="text-gray-600 mb-6">
              Je moet eerst contacts toevoegen voordat je campaigns kunt versturen.
            </p>
            <button
              onClick={() => navigate('/mailinglist')}
              className="btn-primary"
            >
              Contacts Toevoegen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Nieuwe Campaign"
        onBack={() => navigate('/dashboard')}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-[#231F20] mb-3">
            Hoe wil je starten?
          </h2>
          <p className="text-gray-600 text-lg">
            Kies hoe je jouw nieuwe email campaign wilt maken
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/campaigns/create/from-blog')}
            className="group bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#E94E87] hover:shadow-xl transition-all text-left"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#E94E87] to-[#D63D76] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#231F20] mb-3">
                Maak van blog
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Selecteer een bestaande blog en laat AI automatisch een persoonlijke email genereren met afbeelding en link
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate('/campaigns/create/multi-blog')}
            className="group bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#E94E87] hover:shadow-xl transition-all text-left"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#E94E87] to-[#D63D76] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Newspaper className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#231F20] mb-3">
                Meerdere blogs
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Bundel meerdere blogs in één nieuwsbrief met een voorwoord en per blog een teaser
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate('/campaigns/create/manual')}
            className="group bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#E94E87] hover:shadow-xl transition-all text-left"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#E94E87] to-[#D63D76] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Edit3 className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#231F20] mb-3">
                Zelf typen
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Begin met een lege editor en schrijf je eigen email content met volledige controle over de tekst en opmaak
              </p>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
}
